import {Form as AntForm} from 'antd';
import React, {useEffect} from 'react';

import Form from '../../../../../components/Form/Form';
import MobileModalForm from '../../../../../components/Form/MobileModalForm';
import * as formInputNames from '../../../../../consts/form/formInputNames';
import * as localizationKeys from '../../../../../consts/localization/localizationKeys';
import * as modalTypes from '../../../../../consts/modals/modalTypes';
import useIsMobileViewport from '../../../../../hooks/useIsMobileViewport';
import {getLocalizedStrings} from '../../../../../services/localization/localizationService';
import log from '../../../../../services/logger/log';
import organizationsService from '../../../../../services/organizations/organizationsService';
import {setCurrentBuilding} from '../../../../../state/ducks/buildings';
import {setOrganizationsNames} from '../../../../../state/ducks/organizations';
import helpers from '../../../../../utils/helpers';
import buildingModalService from './organizationBuildingModalService';

const OrganizationBuildingModal = (props) => {
    const isMobile = useIsMobileViewport();
    const [form] = AntForm.useForm();
    const {modalType, onCancel, onOk, dispatch} = props || {};
    const usesMobileBuildingLayout = [
        modalTypes.CREATE_BUILDING_MODAL,
        modalTypes.EDIT_BUILDING_MODAL,
        modalTypes.COPY_BUILDING_MODAL,
    ].includes(modalType);

    const {formFields, title, submitButton, cancelButton, initialValues} = buildingModalService.getFormContent(
        modalType,
        form
    );

    useEffect(() => {
        if (!usesMobileBuildingLayout) return undefined;

        document.body.classList.add('building-modal-open');

        return () => document.body.classList.remove('building-modal-open');
    }, [usesMobileBuildingLayout]);

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);
        }

        return () => {
            form.resetFields();
            dispatch(setCurrentBuilding(null));
        };
    }, []);

    organizationsService.getOrganizationsNames();

    const updateOrganizationsForSelects = async () => {
        const organization1 = form.getFieldValue(formInputNames.ORGANIZATION_ID);
        const organization2 = form.getFieldValue(formInputNames.ORGANIZATION_2);
        const organization3 = form.getFieldValue(formInputNames.ORGANIZATION_3);
        const organization4 = form.getFieldValue(formInputNames.ORGANIZATION_4);

        const excludedOrganizations = [organization1, organization2, organization3, organization4].filter(Boolean);

        const organizations = await organizationsService.getFilteredOrganizations(excludedOrganizations);

        dispatch(setOrganizationsNames(organizations));
    };

    const handleRadioChange = async (value, name) => {
        setFormField(name, value);

        if (name === formInputNames.ADDITIONAL_ORGANIZATIONS && value == 'false') {
            resetFormField([
                formInputNames.ORGANIZATION_2,
                formInputNames.ORGANIZATION_3,
                formInputNames.ORGANIZATION_4,
            ]);
        }
    };

    const onFinish = async () => {
        try {
            const values = await form.validateFields();

            helpers.runFunction(onOk, values);
        } catch (error) {
            log.info('Validate Failed:', error);
        }
    };

    const setFormField = (name, value) => form.setFieldsValue({[name]: value});

    const resetFormField = (fieldNames) => {
        const fieldsToReset = {};

        fieldNames.forEach((name) => (fieldsToReset[name] = null));
        form.setFieldsValue(fieldsToReset);
    };

    const handleSelect = async (value, name, option) => {
        const currentValue = form.getFieldValue(name);

        const allSelectedValues = [
            form.getFieldValue(formInputNames.ORGANIZATION_ID),
            form.getFieldValue(formInputNames.ORGANIZATION_2),
            form.getFieldValue(formInputNames.ORGANIZATION_3),
            form.getFieldValue(formInputNames.ORGANIZATION_4),
        ];

        if (allSelectedValues.includes(option.value)) {
            log.info(`Duplicate value selected for ${name}: ${option.value}`);
            return;
        }

        if (currentValue?.value === option.value) return;

        setFormField(name, value);

        if (
            [formInputNames.ORGANIZATION_2, formInputNames.ORGANIZATION_3, formInputNames.ORGANIZATION_4].includes(name)
        ) {
            await updateOrganizationsForSelects();
        }

        switch (name) {
            case formInputNames.CITY_ID: {
                resetFormField([
                    formInputNames.ORGANIZATION_ID,
                    formInputNames.ADDRESS,
                    formInputNames.LATITUDE,
                    formInputNames.LONGITUDE,
                ]);

                await organizationsService.getOrganizationsNames(value);
                break;
            }
            default:
                break;
        }
    };

    const handleChange = () => {};

    if (isMobile) {
        return (
            <MobileModalForm
                form={form}
                formName={`building-mobile-${modalType}`}
                className='organization-building-mobile-form'
                title={title}
                closeLabel={getLocalizedStrings()[localizationKeys.BUILDING_FORM_CLOSE]}
                formFields={formFields}
                initialValues={initialValues}
                onFinish={onFinish}
                onCancel={onCancel}
                submitButton={submitButton}
                cancelButton={cancelButton}
                handleRadioChange={handleRadioChange}
                handleSelect={handleSelect}
                handleChange={handleChange}
            />
        );
    }

    return (
        <Form
            handleRadioChange={handleRadioChange}
            handleSelect={handleSelect}
            closeModal={onCancel}
            initialValues={initialValues}
            submitButton={submitButton}
            onFinish={onFinish}
            title={title}
            formFields={formFields.map((field) => ({...field, preserve: true}))}
            cancelButton={cancelButton}
            formInstance={form}
            handleChange={handleChange}
            formClassName={usesMobileBuildingLayout ? 'building-form' : undefined}
            containerClassName={usesMobileBuildingLayout ? 'building-form-content' : undefined}
            buttonsClassName={usesMobileBuildingLayout ? 'building-form-actions' : undefined}
        />
    );
};

export default React.memo(OrganizationBuildingModal);
